import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import JwalekLogo from '@/components/JwalekLogo';
import SocialLoginButtons from '@/components/SocialLoginButtons';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t } = useLanguage();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ name, email, password });
      toast.success(t('register.welcomeToast', 'Welcome to Jwalek!'));
      nav('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('register.failed', 'Registration failed'));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <Card className="jw-card p-8">
        <div className="text-center">
          <JwalekLogo size={32} />
          <h1 className="font-display text-2xl font-bold mt-4">{t('register.createAccount', 'Create your account')}</h1>
          <p className="text-white/60 text-sm mt-1">{t('register.startInSeconds', 'Start designing in seconds')}</p>
        </div>
        <div className="mt-6"><SocialLoginButtons /></div>
        <div className="my-6 flex items-center gap-3" style={{ color: 'var(--jw-text-subtle)' }}>
          <div className="flex-1 h-px" style={{ background: 'var(--jw-border)' }} />
          <span className="text-xs uppercase tracking-wider">{t('login.orWithEmail', 'or with email')}</span>
          <div className="flex-1 h-px" style={{ background: 'var(--jw-border)' }} />
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm text-white/80">{t('contact.name', 'Name')}</label>
            <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 bg-white/[0.04] border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/80">{t('contact.email', 'Email')}</label>
            <Input data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 bg-white/[0.04] border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/80">{t('login.password', 'Password')}</label>
            <Input data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1 bg-white/[0.04] border-white/10 text-white" />
          </div>
          <Button type="submit" disabled={loading} data-testid="register-submit-button" className="jw-btn-glossy w-full rounded-[14px] h-11">
            {loading ? t('register.creating', 'Creating account...') : t('register.submit', 'Create account')}
          </Button>
        </form>
        <p className="text-center text-white/60 text-sm mt-6">{t('register.haveAccount', 'Already have an account?')} <Link to="/login" data-testid="register-login-link" className="text-white hover:underline">{t('nav.signIn', 'Sign in')}</Link></p>
      </Card>
    </div>
  );
};

export default Register;
